import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { API_URL, SERVICE_CATEGORIES, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Wrench, Moon, Sun, ArrowLeft, Plus, Edit, Trash2, Loader2,
  Shield, Monitor, Settings, LayoutDashboard, User
} from 'lucide-react';

const CATEGORY_ICONS = {
  preventive: Shield,
  software: Monitor,
  hardware: Wrench
};

export default function ServicesPage() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    category: 'preventive',
    description: '',
    pathologies: [],
    supplies: [],
    base_price: 0,
    warranty_days: 30
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'preventive',
      description: '',
      pathologies: [],
      supplies: [],
      base_price: 0,
      warranty_days: 30
    });
    setEditingService(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (service) => {
    setFormData({
      name: service.name,
      category: service.category,
      description: service.description,
      pathologies: service.pathologies || [],
      supplies: service.supplies || [],
      base_price: service.base_price,
      warranty_days: service.warranty_days
    });
    setEditingService(service);
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.description || formData.base_price <= 0) {
      toast.error('Complete todos los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      if (editingService) {
        await axios.put(`${API_URL}/services/${editingService.service_id}`, formData);
        toast.success('Servicio actualizado');
      } else {
        await axios.post(`${API_URL}/services`, formData);
        toast.success('Servicio creado');
      }
      setShowDialog(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error('Error al guardar servicio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!confirm('¿Eliminar este servicio?')) return;
    try {
      await axios.delete(`${API_URL}/services/${serviceId}`);
      toast.success('Servicio eliminado');
      fetchServices();
    } catch (error) {
      toast.error('Error al eliminar servicio');
    }
  };

  const addArrayItem = (field, value) => {
    if (!value.trim()) return;
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], value.trim()]
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-card border-r border-border z-40 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        <div className="flex flex-col h-full">
          <div className="h-16 flex items-center px-4 border-b border-border">
            <Link to="/admin" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                <Wrench className="w-4 h-4 text-primary-foreground" />
              </div>
              {sidebarOpen && <span className="font-mono font-bold">TechFix Pro</span>}
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-2">
            <Link to="/admin">
              <Button variant="ghost" className="w-full justify-start gap-3">
                <LayoutDashboard className="w-4 h-4" />
                {sidebarOpen && 'Dashboard'}
              </Button>
            </Link>
            <Link to="/admin/orders/new">
              <Button variant="ghost" className="w-full justify-start gap-3">
                <Plus className="w-4 h-4" />
                {sidebarOpen && 'Nueva Orden'}
              </Button>
            </Link>
            <Link to="/admin/services">
              <Button variant="secondary" className="w-full justify-start gap-3">
                <Settings className="w-4 h-4" />
                {sidebarOpen && 'Servicios'}
              </Button>
            </Link>
          </nav>

          <div className="p-4 border-t border-border">
            {sidebarOpen ? (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                </div>
              </div>
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <User className="w-4 h-4 text-primary" />
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-30">
          <div className="h-full px-4 flex items-center justify-between">
            <h1 className="font-mono font-bold text-xl">Catálogo de Servicios</h1>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme}>
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogTrigger asChild>
                  <Button onClick={openNewDialog} data-testid="new-service-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Servicio
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-mono">
                      {editingService ? 'Editar Servicio' : 'Nuevo Servicio'}
                    </DialogTitle>
                    <DialogDescription>
                      Define la ficha técnica completa del servicio
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre del servicio *</Label>
                        <Input
                          placeholder="Ej: Mantenimiento Premium"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          data-testid="service-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Categoría *</Label>
                        <Select 
                          value={formData.category} 
                          onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                        >
                          <SelectTrigger data-testid="service-category">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(SERVICE_CATEGORIES).map(([key, label]) => (
                              <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Descripción del protocolo *</Label>
                      <Textarea
                        placeholder="Describe el proceso técnico del servicio..."
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        data-testid="service-description"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Patologías de Hardware Solucionadas</Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.pathologies.map((item, i) => (
                          <span key={i} className="px-2 py-1 bg-orange-500/10 text-orange-600 dark:text-orange-400 rounded-md text-sm flex items-center gap-1">
                            {item}
                            <button onClick={() => removeArrayItem('pathologies', i)} className="hover:text-red-500">×</button>
                          </span>
                        ))}
                      </div>
                      <Input
                        placeholder="Ej: Sobrecalentamiento (Enter para agregar)"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addArrayItem('pathologies', e.target.value);
                            e.target.value = '';
                          }
                        }}
                        data-testid="service-pathologies"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Insumos & Entregables</Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.supplies.map((item, i) => (
                          <span key={i} className="px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded-md text-sm flex items-center gap-1">
                            {item}
                            <button onClick={() => removeArrayItem('supplies', i)} className="hover:text-red-500">×</button>
                          </span>
                        ))}
                      </div>
                      <Input
                        placeholder="Ej: Pasta térmica premium (Enter para agregar)"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addArrayItem('supplies', e.target.value);
                            e.target.value = '';
                          }
                        }}
                        data-testid="service-supplies"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Precio base (USD) *</Label>
                        <Input
                          type="number"
                          placeholder="85"
                          value={formData.base_price || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                          data-testid="service-price"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Garantía (días)</Label>
                        <Input
                          type="number"
                          placeholder="90"
                          value={formData.warranty_days || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, warranty_days: parseInt(e.target.value) || 30 }))}
                          data-testid="service-warranty"
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDialog(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleSubmit} disabled={saving} data-testid="save-service">
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {editingService ? 'Guardar cambios' : 'Crear servicio'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : services.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-mono font-bold text-lg mb-2">Sin servicios configurados</h3>
                <p className="text-muted-foreground mb-4">
                  Crea tu primer servicio para empezar a recibir órdenes
                </p>
                <Button onClick={openNewDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear primer servicio
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {Object.entries(SERVICE_CATEGORIES).map(([category, categoryName]) => {
                const categoryServices = services.filter(s => s.category === category);
                if (categoryServices.length === 0) return null;
                const Icon = CATEGORY_ICONS[category] || Settings;
                
                return (
                  <div key={category}>
                    <div className="flex items-center gap-2 mb-4">
                      <Icon className="w-5 h-5 text-muted-foreground" />
                      <h2 className="font-mono font-bold text-lg">{categoryName}</h2>
                      <span className="text-xs bg-muted px-2 py-1 rounded-full">
                        {categoryServices.length}
                      </span>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {categoryServices.map(service => (
                        <Card key={service.service_id} className="hover:border-primary/50 transition-colors" data-testid={`service-card-${service.service_id}`}>
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                              <CardTitle className="text-lg">{service.name}</CardTitle>
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={() => openEditDialog(service)}
                                  data-testid={`edit-service-${service.service_id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-destructive"
                                  onClick={() => handleDelete(service.service_id)}
                                  data-testid={`delete-service-${service.service_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {service.description}
                            </p>
                            
                            {service.pathologies.length > 0 && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Patologías</p>
                                <div className="flex flex-wrap gap-1">
                                  {service.pathologies.slice(0, 3).map((p, i) => (
                                    <span key={i} className="text-xs px-1.5 py-0.5 bg-orange-500/10 text-orange-600 dark:text-orange-400 rounded">
                                      {p}
                                    </span>
                                  ))}
                                  {service.pathologies.length > 3 && (
                                    <span className="text-xs text-muted-foreground">+{service.pathologies.length - 3}</span>
                                  )}
                                </div>
                              </div>
                            )}

                            {service.supplies.length > 0 && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Insumos</p>
                                <div className="flex flex-wrap gap-1">
                                  {service.supplies.slice(0, 3).map((s, i) => (
                                    <span key={i} className="text-xs px-1.5 py-0.5 bg-green-500/10 text-green-600 dark:text-green-400 rounded">
                                      {s}
                                    </span>
                                  ))}
                                  {service.supplies.length > 3 && (
                                    <span className="text-xs text-muted-foreground">+{service.supplies.length - 3}</span>
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="flex items-center justify-between pt-2 border-t border-border">
                              <span className="text-2xl font-mono font-bold text-primary">
                                {formatCurrency(service.base_price)}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Garantía: {service.warranty_days} días
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
